import React, { createContext, useState, useEffect, useContext } from 'react'
import {
  fetchPlayersOnTeam,
  PlayersOnTeam,
  saveAllStarVote,
  SubmitAllStarPlayer,
  fetchFullEventBySlugGQL,
  EventGQL,
  PlayerAllStarType,
  SubmitAllStarObj,
} from '../../Api'
import { CURRENT_YEAR, EVENTS_YEAR } from '../../Config/League/league.config'
import { isPastTime } from '../../Utils/logicHelper'
import { checkSatisfyTeamCheck, isLocalStorageEnabled } from '../../Utils'
import { AuthContext } from '../../Api/firebase'
import { PLL_CONFERENCES } from '../../Constants/season'
import { SnackbarContext } from '../SnackbarContext'

type AllStarContextType = {
  allStarEvent: EventGQL | null
  availablePlayers: PlayersOnTeam[]
  selectedPlayers: PlayersOnTeam[]
  selectedTeamOnePlayers: PlayersOnTeam[]
  selectedTeamTwoPlayers: PlayersOnTeam[]
  loading: boolean
  closed: boolean
  playerType: PlayerAllStarType
  showLoginPrompt: boolean
  selectPlayer: (player: PlayersOnTeam) => boolean
  removePlayer: (player: PlayersOnTeam) => boolean
  saveAllPicks: (email: string) => Promise<boolean>
  handlePlayerTypeChange: (pType: PlayerAllStarType) => void
  toggleLoginPrompt: () => void
}

export const AllStarContext = createContext<AllStarContextType | null>(null)

export const AllStarProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const asgCloseDate = 1719806399
  const TEAM_LIMIT = 8
  const storageAccess = isLocalStorageEnabled()
  let { uid, isLoggedIn } = useContext(AuthContext)!
  let { toggleShowSnackbar } = useContext(SnackbarContext)!
  const [allStarEvent, setAllStarEvent] = useState<EventGQL | null>(null)
  const [availablePlayers, setAvailablePlayers] = useState<PlayersOnTeam[]>([])
  const [selectedPlayers, setSelectedPlayers] = useState<PlayersOnTeam[]>([]) //Full List of selected players
  //EAST
  const [selectedTeamOnePlayers, setSelectedTeamOnePlayers] = useState<PlayersOnTeam[]>(
    []
  )
  // WEST
  const [selectedTeamTwoPlayers, setSelectedTeamTwoPlayers] = useState<PlayersOnTeam[]>(
    []
  )
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [closed, setClosed] = useState<boolean>(false)
  const [playerType, setPlayerType] = useState<PlayerAllStarType>('east')
  const [showLoginPrompt, setShowLoginPrompt] = useState<boolean>(false)

  useEffect(() => {
    fetchAllStarData()
  }, [])

  const fetchAllStarData = async () => {
    await Promise.all([fetchAllStarEvent(), fetchAvailablePlayers()])

    //Clearing old local storage
    if (storageAccess) {
      localStorage.removeItem('selectedPlayers')
    }

    setLoading(false)
  }

  const fetchAllStarEvent = async () => {
    const allStarEvent = await fetchFullEventBySlugGQL('2024_all_star_game')
    if (allStarEvent) {
      setAllStarEvent(allStarEvent)
    }
  }

  const fetchAvailablePlayers = async () => {
    let isClosed = isPastTime(asgCloseDate)
    if (isClosed) {
      setClosed(true)
      return Promise.resolve()
    }
    //Check for if user previously selected
    let storedIds = checkLocalStorage()

    //Fetch all selection options
    const players = await fetchPlayersOnTeam(EVENTS_YEAR)
    if (!players || players.allPlayers.length < 1) return

    //Find selected players from available using ids
    if (storedIds.length > 0) {
      getSelectedPlayers(storedIds, players.allPlayers)
    }

    //Filter out players with no games, then sort
    const sorted = players.allPlayers
      .filter((p) => p.stats?.gamesPlayed > 0)
      .sort(
        (a, b) =>
          b.stats.points - a.stats.points ||
          b.stats?.faceoffsWon - a.stats?.faceoffsWon ||
          b.stats?.saves - a.stats?.saves ||
          b.stats?.groundBalls - a.stats?.groundBalls
      )
    setAvailablePlayers(sorted)
  }

  const checkLocalStorage = () => {
    if (!storageAccess) return []
    const item = localStorage.getItem('selectedPlayersASG24')
    if (item) {
      let ids = JSON.parse(item)
      setSelectedIds(ids)
      return ids
    } else {
      //No players, fetch
      console.log('No selected players')
      return []
    }
  }

  const getSelectedPlayers = (playerIds: string[], availPls: PlayersOnTeam[]) => {
    let east: PlayersOnTeam[] = []
    let west: PlayersOnTeam[] = []
    if (playerIds.length > 0) {
      playerIds.map((id) => {
        let foundPlayer = availPls.find((pl) => pl.officialId === id)
        if (!foundPlayer) {
          return
        } else {
          const conf = foundPlayer?.currentTeam?.conference
          return conf === PLL_CONFERENCES.WESTERN
            ? west.push(foundPlayer)
            : east.push(foundPlayer)
        }
      })
    }
    setSelectedTeamOnePlayers(east)
    setSelectedTeamTwoPlayers(west)
    return setSelectedPlayers([...west, ...east])
  }

  const selectPlayer = (player: PlayersOnTeam) => {
    let validCheckResult = checkIfValidSelection(player)
    if (!validCheckResult.valid) {
      toggleShowSnackbar({
        text: validCheckResult.text,
        color: 'red',
      })
      return false
    }
    const conf = player?.currentTeam?.conference
    let sel = [...selectedPlayers, player]
    let selIds = [...selectedIds, player.officialId]
    if (conf === PLL_CONFERENCES.EASTERN) {
      setSelectedTeamOnePlayers((prev) => [...prev, player])
    } else {
      setSelectedTeamTwoPlayers((prev) => [...prev, player])
    }
    setSelectedPlayers(sel)
    setSelectedIds(selIds)
    storageAccess && localStorage.setItem('selectedPlayersASG24', JSON.stringify(selIds))
    return true
  }

  const removePlayer = (player: PlayersOnTeam) => {
    console.log('Removing')
    const conf = player?.currentTeam?.conference
    let idList = [...selectedIds]
    let pList = [...selectedPlayers]
    pList = pList.filter((p) => p.officialId !== player.officialId)
    idList = idList.filter((id) => id !== player.officialId)
    if (conf === PLL_CONFERENCES.EASTERN) {
      setSelectedTeamOnePlayers((prev) =>
        prev.filter((p) => p.officialId !== player.officialId)
      )
    } else {
      setSelectedTeamTwoPlayers((prev) =>
        prev.filter((p) => p.officialId !== player.officialId)
      )
    }
    setSelectedPlayers(pList)
    setSelectedIds(idList)
    storageAccess && localStorage.setItem('selectedPlayersASG24', JSON.stringify(idList))
    return true
  }

  const saveAllPicks = async (email: string) => {
    console.log('Saving')
    if (closed) {
      toggleShowSnackbar({
        text: 'Voting is closed',
        color: 'red',
      })
      return Promise.resolve(false)
    }
    if (selectedPlayers.length < 6) {
      toggleShowSnackbar({
        text: 'Please select more players',
        color: 'red',
      })
      return Promise.resolve(false)
    }
    const mappedTeamOne = mapPlayerSelections([...selectedTeamOnePlayers])
    const mappedTeamTwo = mapPlayerSelections([...selectedTeamTwoPlayers])
    const mappedSelections = [...mappedTeamOne, ...mappedTeamTwo]
    let input: SubmitAllStarObj = {
      email: email,
      year: CURRENT_YEAR,
      votes: mappedSelections,
    }
    if (isLoggedIn && uid) {
      input.firebaseId = uid //Add user id if logged in
    }
    try {
      const result = await saveAllStarVote(input)
      if (!result || result.votes?.length < 1) return false
      let now = Date.now() / 1000
      storageAccess && localStorage.setItem('allStarVoteDate', now.toString())
      return true
    } catch (e: any) {
      console.log(e)
      toggleShowSnackbar({
        text: 'Error submitting vote',
        color: 'red',
      })
      return false
    }
  }

  const mapPlayerSelections = (list: PlayersOnTeam[]) => {
    let pList: SubmitAllStarPlayer[] = []
    list.map((p) => {
      const mappedPlayer = {
        playerId: p.officialId,
        teamId: p.currentTeam.officialId,
        position: p.currentTeam.position,
      }
      pList.push(mappedPlayer)
      return
    })
    return pList
  }

  const checkIfValidSelection = (player: PlayersOnTeam) => {
    const conf = player?.currentTeam?.conference
    const selectionsForTeam =
      conf === PLL_CONFERENCES.EASTERN ? selectedTeamOnePlayers : selectedTeamTwoPlayers
    if (conf === PLL_CONFERENCES.EASTERN) {
      if (selectedTeamOnePlayers.length === TEAM_LIMIT) {
        //Limit max players
        return {
          valid: false,
          text: 'Eastern team is already full',
        }
      }
    }
    if (conf === PLL_CONFERENCES.WESTERN) {
      if (selectedTeamTwoPlayers.length === TEAM_LIMIT) {
        return {
          valid: false,
          text: 'Western team is already full',
        }
      }
    }

    const result = checkSatisfyTeamCheck(player, selectionsForTeam, TEAM_LIMIT)
    return result
  }

  const handlePlayerTypeChange = (pType: PlayerAllStarType) => {
    setPlayerType(pType)
  }

  const toggleLoginPrompt = () => {
    setShowLoginPrompt(!showLoginPrompt)
  }

  return (
    <AllStarContext.Provider
      value={{
        allStarEvent,
        availablePlayers,
        selectedPlayers,
        selectedTeamOnePlayers,
        selectedTeamTwoPlayers,
        loading,
        closed,
        playerType,
        showLoginPrompt,
        saveAllPicks,
        selectPlayer,
        removePlayer,
        handlePlayerTypeChange,
        toggleLoginPrompt,
      }}
    >
      {children}
    </AllStarContext.Provider>
  )
}
